import * as React from 'react';
import CommonTable, { ICommonTable } from 'src/components/common/CommonTable';
import { ColumnProps } from 'antd/lib/table';
import I18N from 'src/services/I18NService';
import { IFreightTemplateData } from 'src/entity/SettingEntity';
import { ILocationInfo } from 'src/entity/LocationEntity';
import { Modal } from 'antd';
import FreightEditForm from './FreightEditForm';
import * as uuid from 'uuid';
import { IFreightTemplate, EnumFreightFreeCondition } from 'src/entity/DrugStoreEntity';
import { isArray } from 'lodash';

interface IFreightTemplateSettingProps {
  /** 省份列表 */
  locations: ILocationInfo[];
  /** 传入的数据 */
  value?: IFreightTemplateData[];
  /** 是否为详情(不可编辑) */
  isDetail?: boolean;
  /** 回调 */
  onChange?: (value: IFreightTemplateData[]) => void;
}

interface IFreightTemplateSettingState extends ICommonTable<IFreightTemplateData> {
  /** 编辑窗口状态 */
  freightEditVisible: boolean;
  /** 选中的编辑数据索引 */
  selectedIndex: number | undefined;
}

class FreightTemplateSetting extends React.Component<IFreightTemplateSettingProps, IFreightTemplateSettingState> {
  constructor(props: IFreightTemplateSettingProps) {
    super(props);

    this.state = {
      rowKey: 'rowKey',
      loadingTable: false,
      columns: [],
      dataSource: props.value || [],
      freightEditVisible: false,
      selectedIndex: undefined,
    } as IFreightTemplateSettingState;
  }

  componentDidMount() {
    const columns: Array<ColumnProps<IFreightTemplateData>> = [
      {
        title: I18N.get('setting_fee_template_area'),
        key: 'locations',
        dataIndex: 'locations',
        width: '55%',
        render: (text: any, record: IFreightTemplateData, index: number): React.ReactNode => {
          return this.getLocationDesc(record.locations || []);
        },
      },
      {
        title: I18N.get('setting_fee_template_free_condition'),
        key: 'free_condition',
        dataIndex: 'free_condition',
        width: '15%',
        render: (text: any, record: IFreightTemplateData, index: number): React.ReactNode => {
          return !record.enable
            ? `${I18N.get('common_status_not_start')}`
            : `${I18N.get('common_status_start')}，${I18N.get('setting_fee_template_free_condition_prefix', false)}${record.fee}${I18N.get(
              'setting_fee_template_free_condition_suffix',
              false
            )}`;
        },
      },
      {
        title: I18N.get('setting_fee_template_bill_method'),
        key: 'bill_method',
        dataIndex: 'bill_method',
        width: '15%',
        render: (text: any, record: IFreightTemplateData, index: number): React.ReactNode => {
          return `${I18N.get('setting_fee_template_normal_rule')}${record.weight_start}kg${record.fee_start}${I18N.get('common_unit_yuan')}, ${I18N.get(
            'setting_fee_template_unusual_rule'
          )}${record.weight_left}kg${record.fee_left}${I18N.get('common_unit_yuan')}`;
        },
      },
      {
        title: I18N.get('common_operation'),
        dataIndex: 'operation',
        key: 'operation',
        render: (text: any, record: IFreightTemplateData, index: number): React.ReactNode => {
          return (
            <div>
              <a onClick={() => this.editTemplate(index)}>{`${I18N.get('common_edit')}`}</a>
              &nbsp;&nbsp;
              <a onClick={() => this.deleteTemplate(index)}>{I18N.get('common_delete')}</a>
            </div>
          );
        },
      },
    ];

    const { isDetail } = this.props;
    if (isDetail) {
      // 去掉操作列
      columns.pop();
    }

    this.setState({
      columns,
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps: IFreightTemplateSettingProps) {
    this.setState({
      dataSource: nextProps.value || [],
    });
  }

  /**
   * 获取地区描述
   *
   * @memberof FreightTemplateSetting
   */
  getLocationDesc = (locationIDList: number[]): string => {
    const { locations } = this.props;
    const locationDescSet = new Set<string>();
    for (const locationID of locationIDList || []) {
      const obj = locations.find((t) => t.id === locationID);
      if (!!obj && !!obj.name) {
        locationDescSet.add(obj.name);
      }
    }
    return Array.from(locationDescSet).join(', ');
  };

  /**
   * 表格页脚
   *
   * @memberof FreightTemplateSetting
   */
  tableFooter = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <a onClick={() => this.handleVisible(true)}>{I18N.get('common_add')}</a>
        <span>{I18N.get('setting_fee_template_tips')}</span>
      </div>
    );
  };

  /**
   * 编辑窗口是否可见
   *
   * @memberof FreightTemplateSetting
   */
  handleVisible = (visible: boolean) => {
    this.setState({
      freightEditVisible: visible,
      selectedIndex: undefined,
    });
  };

  /**
   * 点击编辑时触发
   *
   * @memberof FreightTemplateSetting
   */
  editTemplate = (index: number) => {
    this.setState({
      freightEditVisible: true,
      selectedIndex: index,
    });
  };

  /**
   * 点击删除触发
   *
   * @memberof FreightTemplateSetting
   */
  deleteTemplate = (index: number) => {
    const dataSource = this.state.dataSource || [];
    if (index < dataSource.length) {
      dataSource.splice(index, 1);
    }
    this.setState({
      dataSource,
    });
    this.triggerChange(dataSource);
  };

  /**
   * 编辑窗口保存时触发
   *
   * @memberof FreightTemplateSetting
   */
  freightDataChange = (data: IFreightTemplateData) => {
    const { selectedIndex, dataSource } = this.state;
    if (!!dataSource) {
      if (selectedIndex !== undefined && dataSource.length > selectedIndex) {
        dataSource[selectedIndex] = data;
      } else {
        dataSource.push(data);
      }
    }
    this.setState({
      dataSource,
      selectedIndex: undefined,
      freightEditVisible: false,
    });
    this.triggerChange(dataSource || []);
  };

  /**
   * 回调
   *
   * @memberof FreightTemplateSetting
   */
  triggerChange = (changedValue: IFreightTemplateData[]) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(changedValue);
    }
  };

  renderTable = () => {
    const { isDetail } = this.props;
    if (isDetail) {
      return (
        <CommonTable
          rowKey={this.state.rowKey}
          showPagination={false}
          columns={this.state.columns}
          loadingTable={this.state.loadingTable}
          dataSource={this.state.dataSource}
        />
      )
    } else {
      return (
        <CommonTable
          rowKey={this.state.rowKey}
          showPagination={false}
          columns={this.state.columns}
          loadingTable={this.state.loadingTable}
          dataSource={this.state.dataSource}
          footer={() => this.tableFooter()}
        />
      )
    }
  }

  render() {
    let freightData: IFreightTemplateData | undefined;
    const disabledLocations: number[] = [];

    const { selectedIndex, dataSource } = this.state;
    if (!!dataSource) {
      if (selectedIndex !== undefined && dataSource.length > selectedIndex) {
        freightData = dataSource[selectedIndex];
      }
      for (let i = 0; i < dataSource.length; i++) {
        if (i !== selectedIndex) {
          for (const tempID of dataSource[i].locations || []) {
            disabledLocations.push(tempID);
          }
        }
      }
    }

    const formKey = !!freightData ? freightData.rowKey : uuid.v4();

    const commonTable = this.renderTable();
    return (
      <div>
        {commonTable}
        <Modal
          title={`${I18N.get('setting_fee_template')}`}
          destroyOnClose={true}
          visible={this.state.freightEditVisible}
          maskClosable={false}
          width={'50%'}
          footer={null}
          onCancel={() => this.handleVisible(false)}
        >
          <FreightEditForm
            key={formKey}
            locations={this.props.locations}
            disabledLocations={disabledLocations}
            freightData={freightData}
            onChange={this.freightDataChange}
          />
        </Modal>
      </div>
    );
  }
}

export default FreightTemplateSetting;

/**
 * 后台运费模板数据转换为界面数据
 *
 * @export
 * @param {IFreightTemplate[]} dataList
 * @returns {IFreightTemplateData[]}
 */
export function freightTemplate2PageData(dataList: IFreightTemplate[]): Array<{
  template_name: string;
  freight_template_content: IFreightTemplateData[];
  medicine_group_name: string;
  is_default: boolean
}> {
  return dataList.map((data) => ({
    template_name: data.template_name,
    freight_template_content: (data.freight_template_content || []).map(template => ({
      locations: template.regions,
      enable: template.free_unit !== EnumFreightFreeCondition.不包邮,
      fee: template.free_amount,
      fee_start: template.initial_price,
      weight_start: template.initial_weight / 1000,
      fee_left: template.extra_price,
      weight_left: template.extra_weight / 1000,
      rowKey: uuid.v4(),
    })),
    medicine_group_name: isArray(data.medicine_group_infos) ?(data.medicine_group_infos || []).map((info) => info.name).join('，'): '',
    is_default: data.is_default
  }))
}

/**
 * 界面设置的模板数据转换为后台数据
 *
 * @export
 * @param {IFreightTemplateData[]} dataList
 * @returns {IFreightTemplate[]}
 */
// export function pageData2FreightTemplate(dataList: IFreightTemplateData[]): IFreightTemplate[] {
//   const result: IFreightTemplate[] = [];
//   for (const data of dataList) {
//     result.push({
//       regions: data.locations,
//       free_amount: data.fee,
//       free_unit: data.enable ? EnumFreightFreeCondition.满XX元包邮 : EnumFreightFreeCondition.不包邮,
//       initial_weight: data.weight_start * 1000,
//       initial_price: data.fee_start,
//       extra_weight: data.weight_left * 1000,
//       extra_price: data.fee_left,
//     });
//   }
//   return result;
// }



// WEBPACK FOOTER //
// ./src/components/views/Setting/FreightTemplateSetting.tsx